import {
  GltfReferenceOutOfBoundsError,
  InvalidGltfManifestError,
  MeshLoadCancelledError,
} from '@/plugins/mesh/errors'
import type { GltfManifestReader } from '@/plugins/mesh/loaders/gltfLowMemory/interfaces'
import { validateGltfManifest } from '@/plugins/mesh/loaders/gltfLowMemory/manifestValidation'
import type {
  GlbChunkInfo,
  GlbContainerInfo,
  GltfBufferLocation,
  GltfManifest,
  GltfManifestDocument,
} from '@/plugins/mesh/loaders/gltfLowMemory/types'
import { describeMeshSource, resolveMeshBaseUrl } from '@/plugins/mesh/loaders/loaderUtils'
import type { MeshLoadRequest, MeshSourceData } from '@/plugins/mesh/types'

const GLB_MAGIC = 0x46546c67
const GLB_VERSION = 2
const JSON_CHUNK_TYPE = 0x4e4f534a
const BIN_CHUNK_TYPE = 0x004e4942
const GLB_HEADER_BYTES = 12
const CHUNK_HEADER_BYTES = 8

interface ParsedGlb {
  readonly manifest: GltfManifest
  readonly glb: GlbContainerInfo
}

interface RangeResponse {
  readonly bytes: Uint8Array
  readonly fullResponse: boolean
}

function throwIfCancelled(signal: AbortSignal | undefined, source: string): void {
  if (signal?.aborted) {
    throw new MeshLoadCancelledError(source, { cause: signal.reason })
  }
}

function toBytes(data: ArrayBuffer | ArrayBufferView): Uint8Array {
  return ArrayBuffer.isView(data)
    ? new Uint8Array(data.buffer, data.byteOffset, data.byteLength)
    : new Uint8Array(data)
}

async function readSourceBytes(data: Exclude<MeshSourceData, string>): Promise<Uint8Array> {
  if (data instanceof Blob) return new Uint8Array(await data.arrayBuffer())
  return toBytes(data)
}

function parseJsonText(text: string, source: string): GltfManifest {
  try {
    let normalized = text.trim()
    while (normalized.charCodeAt(normalized.length - 1) === 0) {
      normalized = normalized.slice(0, -1).trimEnd()
    }
    const value = JSON.parse(normalized)
    return validateGltfManifest(value, source)
  } catch (error) {
    if (
      error instanceof InvalidGltfManifestError ||
      error instanceof GltfReferenceOutOfBoundsError
    ) {
      throw error
    }
    throw new InvalidGltfManifestError(
      source,
      error instanceof Error ? error.message : String(error),
      error
    )
  }
}

function createDataView(bytes: Uint8Array): DataView {
  return new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength)
}

function copyBytesToArrayBuffer(bytes: Uint8Array): ArrayBuffer {
  if (
    bytes.byteOffset === 0 &&
    bytes.byteLength === bytes.buffer.byteLength &&
    bytes.buffer instanceof ArrayBuffer
  ) {
    return bytes.buffer
  }
  return bytes.slice().buffer as ArrayBuffer
}

function validateGlbHeader(view: DataView, byteLength: number, source: string): number {
  if (byteLength < GLB_HEADER_BYTES) {
    throw new InvalidGltfManifestError(source, 'GLB header 长度不足')
  }
  if (view.getUint32(0, true) !== GLB_MAGIC) {
    throw new InvalidGltfManifestError(source, 'GLB magic 无效')
  }
  const version = view.getUint32(4, true)
  if (version !== GLB_VERSION) {
    throw new InvalidGltfManifestError(source, `GLB 版本必须为 2，收到 ${version}`)
  }
  const totalLength = view.getUint32(8, true)
  if (totalLength < GLB_HEADER_BYTES || totalLength % 4 !== 0) {
    throw new InvalidGltfManifestError(source, `GLB 总长度无效: ${totalLength}`)
  }
  return totalLength
}

function readChunkHeader(
  view: DataView,
  headerOffset: number,
  totalLength: number,
  source: string
): GlbChunkInfo {
  if (headerOffset + CHUNK_HEADER_BYTES > totalLength) {
    throw new InvalidGltfManifestError(source, `GLB chunk header 越界: ${headerOffset}`)
  }
  const byteLength = view.getUint32(headerOffset, true)
  const type = view.getUint32(headerOffset + 4, true)
  if (byteLength % 4 !== 0) {
    throw new InvalidGltfManifestError(source, `GLB chunk 未按 4 字节对齐: ${byteLength}`)
  }
  const byteOffset = headerOffset + CHUNK_HEADER_BYTES
  if (byteOffset + byteLength > totalLength) {
    throw new InvalidGltfManifestError(source, `GLB chunk 超出声明总长度: ${byteOffset}`)
  }
  return { type, byteOffset, byteLength }
}

/** 解析内存 GLB，验证 header、chunk 长度和 4 字节对齐。 */
export function parseGlbContainer(bytes: Uint8Array, source: string): ParsedGlb {
  const view = createDataView(bytes)
  const totalLength = validateGlbHeader(view, bytes.byteLength, source)
  if (totalLength !== bytes.byteLength) {
    throw new InvalidGltfManifestError(
      source,
      `GLB 声明总长度 ${totalLength} 与实际 ${bytes.byteLength} 不一致`
    )
  }

  const chunks: GlbChunkInfo[] = []
  let headerOffset = GLB_HEADER_BYTES
  while (headerOffset < totalLength) {
    const chunk = readChunkHeader(view, headerOffset, totalLength, source)
    chunks.push(chunk)
    headerOffset = chunk.byteOffset + chunk.byteLength
  }
  if (headerOffset !== totalLength || chunks.length === 0 || chunks[0].type !== JSON_CHUNK_TYPE) {
    throw new InvalidGltfManifestError(source, 'GLB 首个 chunk 必须是 JSON chunk')
  }

  const jsonChunk = chunks[0]
  const binaryChunks = chunks.filter((chunk) => chunk.type === BIN_CHUNK_TYPE)
  if (binaryChunks.length > 1) {
    throw new InvalidGltfManifestError(source, 'GLB 只能包含一个 BIN chunk')
  }
  const jsonText = new TextDecoder().decode(
    bytes.subarray(jsonChunk.byteOffset, jsonChunk.byteOffset + jsonChunk.byteLength)
  )
  return {
    manifest: parseJsonText(jsonText, source),
    glb: {
      version: 2,
      totalLength,
      jsonChunk,
      binaryChunk: binaryChunks[0],
      unknownChunks: chunks.slice(1).filter((chunk) => chunk.type !== BIN_CHUNK_TYPE),
    },
  }
}

function resolveExternalUrl(uri: string, baseUrl: string, source: string): string {
  try {
    if (baseUrl) return new URL(uri, baseUrl).href
    const absolute = new URL(uri)
    if (!['http:', 'https:', 'blob:'].includes(absolute.protocol)) {
      throw new Error(`不支持 URI scheme: ${absolute.protocol}`)
    }
    return absolute.href
  } catch (error) {
    if (!/^[a-z][a-z\d+.-]*:/iu.test(uri)) return uri
    throw new InvalidGltfManifestError(source, `外部 Buffer URI 无效: ${uri}`, error)
  }
}

export function createBufferLocations(
  manifest: GltfManifest,
  format: 'gltf' | 'glb',
  baseUrl: string,
  source: string,
  glb?: GlbContainerInfo
): readonly GltfBufferLocation[] {
  return (manifest.buffers ?? []).map((buffer, bufferIndex) => {
    if (buffer.uri?.startsWith('data:')) {
      return {
        kind: 'data-uri',
        bufferIndex,
        uri: buffer.uri,
        byteLength: buffer.byteLength,
      }
    }
    if (buffer.uri) {
      return {
        kind: 'external',
        bufferIndex,
        url: resolveExternalUrl(buffer.uri, baseUrl, source),
        byteLength: buffer.byteLength,
      }
    }
    if (format !== 'glb' || bufferIndex !== 0 || !glb?.binaryChunk) {
      throw new InvalidGltfManifestError(
        source,
        `buffers[${bufferIndex}] 缺少 uri 且没有对应 GLB BIN chunk`
      )
    }
    const paddingBytes = glb.binaryChunk.byteLength - buffer.byteLength
    if (paddingBytes < 0 || paddingBytes > 3) {
      throw new GltfReferenceOutOfBoundsError(
        source,
        `buffers[${bufferIndex}] 长度 ${buffer.byteLength} 与 BIN chunk ${glb.binaryChunk.byteLength} 不一致`
      )
    }
    return {
      kind: 'glb-bin',
      bufferIndex,
      byteOffset: glb.binaryChunk.byteOffset,
      byteLength: buffer.byteLength,
    }
  })
}

async function fetchFullSource(
  request: MeshLoadRequest,
  signal: AbortSignal | undefined,
  source: string
): Promise<Uint8Array> {
  if (!request.url) throw new InvalidGltfManifestError(source, '请求缺少 url 或 data')
  let response: Response
  try {
    response = await fetch(request.url, {
      signal,
      headers: request.headers,
      credentials: request.credentials,
    })
  } catch (error) {
    throwIfCancelled(signal, source)
    throw error
  }
  if (!response.ok) {
    throw new InvalidGltfManifestError(
      source,
      `主资产请求失败: ${response.status} ${response.statusText}`
    )
  }
  const bytes = new Uint8Array(await response.arrayBuffer())
  throwIfCancelled(signal, source)
  request.onProgress?.(1)
  return bytes
}

async function fetchRange(
  request: MeshLoadRequest,
  start: number,
  end: number,
  signal: AbortSignal | undefined,
  source: string
): Promise<RangeResponse> {
  if (!request.url) throw new InvalidGltfManifestError(source, 'GLB Range 请求缺少 url')
  const headers = new Headers(request.headers)
  headers.set('Range', `bytes=${start}-${end}`)
  let response: Response
  try {
    response = await fetch(request.url, {
      signal,
      headers,
      credentials: request.credentials,
    })
  } catch (error) {
    throwIfCancelled(signal, source)
    throw error
  }
  if (!response.ok) {
    throw new InvalidGltfManifestError(source, `GLB Range 请求失败: ${response.status}`)
  }
  const bytes = new Uint8Array(await response.arrayBuffer())
  throwIfCancelled(signal, source)
  if (response.status === 200) return { bytes, fullResponse: true }
  if (response.status !== 206 || bytes.byteLength !== end - start + 1) {
    throw new InvalidGltfManifestError(source, 'GLB Range 响应长度或状态无效')
  }
  const contentRange = response.headers.get('content-range')
  if (contentRange && !contentRange.startsWith(`bytes ${start}-${end}/`)) {
    throw new InvalidGltfManifestError(source, `GLB Content-Range 无效: ${contentRange}`)
  }
  return { bytes, fullResponse: false }
}

async function readUrlGlbManifest(
  request: MeshLoadRequest,
  signal: AbortSignal | undefined,
  source: string
): Promise<{ parsed: ParsedGlb; sourceData?: ArrayBuffer }> {
  const initial = await fetchRange(request, 0, 19, signal, source)
  if (initial.fullResponse) {
    return {
      parsed: parseGlbContainer(initial.bytes, source),
      sourceData: copyBytesToArrayBuffer(initial.bytes),
    }
  }

  const initialView = createDataView(initial.bytes)
  const totalLength = validateGlbHeader(initialView, totalLengthFromRange(initial.bytes), source)
  const jsonByteLength = initialView.getUint32(12, true)
  const jsonType = initialView.getUint32(16, true)
  if (jsonType !== JSON_CHUNK_TYPE || jsonByteLength % 4 !== 0) {
    throw new InvalidGltfManifestError(source, 'GLB 首个 chunk 必须是对齐的 JSON chunk')
  }
  const jsonEnd = 20 + jsonByteLength - 1
  if (jsonEnd >= totalLength) {
    throw new InvalidGltfManifestError(source, 'GLB JSON chunk 超出总长度')
  }
  const jsonResponse = await fetchRange(request, 20, jsonEnd, signal, source)
  if (jsonResponse.fullResponse) {
    return {
      parsed: parseGlbContainer(jsonResponse.bytes, source),
      sourceData: copyBytesToArrayBuffer(jsonResponse.bytes),
    }
  }

  const jsonChunk: GlbChunkInfo = {
    type: JSON_CHUNK_TYPE,
    byteOffset: 20,
    byteLength: jsonByteLength,
  }
  const unknownChunks: GlbChunkInfo[] = []
  let binaryChunk: GlbChunkInfo | undefined
  let headerOffset = jsonEnd + 1
  while (headerOffset < totalLength) {
    const header = await fetchRange(
      request,
      headerOffset,
      headerOffset + CHUNK_HEADER_BYTES - 1,
      signal,
      source
    )
    if (header.fullResponse) {
      return {
        parsed: parseGlbContainer(header.bytes, source),
        sourceData: copyBytesToArrayBuffer(header.bytes),
      }
    }
    const chunk = readChunkHeader(
      createDataView(header.bytes),
      0,
      totalLength - headerOffset,
      source
    )
    const absoluteChunk = {
      ...chunk,
      byteOffset: headerOffset + CHUNK_HEADER_BYTES,
    }
    if (absoluteChunk.type === BIN_CHUNK_TYPE) {
      if (binaryChunk) throw new InvalidGltfManifestError(source, 'GLB 只能包含一个 BIN chunk')
      binaryChunk = absoluteChunk
    } else {
      unknownChunks.push(absoluteChunk)
    }
    headerOffset = absoluteChunk.byteOffset + absoluteChunk.byteLength
  }
  if (headerOffset !== totalLength) {
    throw new InvalidGltfManifestError(source, 'GLB chunk 链与总长度不一致')
  }

  const jsonText = new TextDecoder().decode(jsonResponse.bytes)
  return {
    parsed: {
      manifest: parseJsonText(jsonText, source),
      glb: {
        version: 2,
        totalLength,
        jsonChunk,
        binaryChunk,
        unknownChunks,
      },
    },
  }
}

function totalLengthFromRange(bytes: Uint8Array): number {
  if (bytes.byteLength < 12) return bytes.byteLength
  return createDataView(bytes).getUint32(8, true)
}

/** 默认的元数据优先 glTF/GLB manifest 读取器。 */
export class DefaultGltfManifestReader implements GltfManifestReader {
  async read(
    request: MeshLoadRequest,
    format: 'gltf' | 'glb',
    signal = request.signal
  ): Promise<GltfManifestDocument> {
    const source = describeMeshSource(request)
    const baseUrl = resolveMeshBaseUrl(request)
    throwIfCancelled(signal, source)

    if (format === 'glb') {
      let parsed: ParsedGlb
      let sourceData: ArrayBuffer | ArrayBufferView | Blob | undefined
      if (request.data !== undefined) {
        if (typeof request.data === 'string') {
          throw new InvalidGltfManifestError(source, 'GLB 内存输入不能是字符串')
        }
        const bytes = await readSourceBytes(request.data)
        parsed = parseGlbContainer(bytes, source)
        sourceData = request.data
      } else {
        const remote = await readUrlGlbManifest(request, signal, source)
        parsed = remote.parsed
        sourceData = remote.sourceData
      }
      throwIfCancelled(signal, source)
      return {
        manifest: parsed.manifest,
        format,
        source,
        baseUrl,
        glb: parsed.glb,
        bufferLocations: createBufferLocations(
          parsed.manifest,
          format,
          baseUrl,
          source,
          parsed.glb
        ),
        sourceData,
      }
    }

    let text: string
    let sourceData: MeshSourceData | undefined = request.data
    if (typeof request.data === 'string') {
      text = request.data
    } else {
      const bytes =
        request.data === undefined
          ? await fetchFullSource(request, signal, source)
          : await readSourceBytes(request.data)
      text = new TextDecoder().decode(bytes)
      sourceData ??= copyBytesToArrayBuffer(bytes)
    }
    throwIfCancelled(signal, source)
    const manifest = parseJsonText(text, source)
    return {
      manifest,
      format,
      source,
      baseUrl,
      bufferLocations: createBufferLocations(manifest, format, baseUrl, source),
      sourceData,
    }
  }
}
